/**
 * Upload Template Dialog
 * Uploads a .docx template with {{Variable_Name}} placeholders.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Loader2, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  { value: 'engagement_letter', label: 'Engagement Letter' },
  { value: 'tax_return_confirmation', label: 'Tax Return Confirmation' },
  { value: 'income_estimate', label: 'Income Estimate' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'solvency_resolution', label: 'Solvency Resolution' },
  { value: 'meeting_minutes', label: 'Meeting Minutes' },
  { value: 'compliance_letter', label: 'Compliance Letter' },
  { value: 'fee_notice', label: 'Fee Notice' },
  { value: 'general', label: 'General' },
];

const MAX_SIZE_BYTES = 5 * 1024 * 1024;

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

interface UploadTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UploadTemplateDialog({ open, onOpenChange }: UploadTemplateDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('general');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const resetForm = () => {
    setName('');
    setCategory('general');
    setDescription('');
    setFile(null);
    setFileError(null);
  };

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file selected');
      const fileBase64 = await fileToBase64(file);
      const resp = await apiClient.post('/automation/templates', {
        name,
        category,
        description: description || undefined,
        fileBase64,
        originalFilename: file.name,
      });
      return resp.data?.data || resp.data;
    },
    onSuccess: (data: any) => {
      const varCount = data?.variables?.length ?? data?.variableCount ?? 0;
      toast.success(`Template uploaded. ${varCount} variables detected.`);
      queryClient.invalidateQueries({ queryKey: ['automation', 'templates'] });
      resetForm();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to upload template');
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (!f.name.toLowerCase().endsWith('.docx')) {
      setFileError('Only .docx files are supported');
      setFile(null);
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      setFileError('File too large (max 5MB)');
      setFile(null);
      return;
    }
    setFile(f);
  };

  const canSubmit = !!name.trim() && !!file && !uploadMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetForm(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Template</DialogTitle>
          <DialogDescription>
            Upload a Word (.docx) template with <span className="font-mono text-xs">{'{{Variable_Name}}'}</span> placeholders. Use <span className="font-mono text-xs">{'{{Variable?}}'}</span> to force user input.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tmpl-name">Name *</Label>
            <Input id="tmpl-name" placeholder="e.g. 2026 Engagement Letter" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tmpl-category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="tmpl-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tmpl-desc">Description</Label>
            <Textarea id="tmpl-desc" placeholder="Optional description…" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </div>

          <div className="space-y-2">
            <Label>Word File (.docx) *</Label>
            {file ? (
              <div className="flex items-center gap-2 rounded-md border p-2 bg-muted/30">
                <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{file.name}</p>
                  <p className="text-[11px] text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setFile(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <label className={cn(
                'flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-md py-6 cursor-pointer hover:bg-muted/40 transition-colors',
                fileError && 'border-destructive'
              )}>
                <Upload className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Click to select a .docx file</span>
                <span className="text-[11px] text-muted-foreground">Max 5MB</span>
                <input type="file" accept=".docx" className="hidden" onChange={handleFileChange} />
              </label>
            )}
            {fileError && <p className="text-xs text-destructive">{fileError}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploadMutation.isPending}>Cancel</Button>
          <Button onClick={() => uploadMutation.mutate()} disabled={!canSubmit}>
            {uploadMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Upload Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UploadTemplateDialog;
